import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

import { UserAnalyticsService } from "@analytics/index";

import useEffectAfterMount from "@hooks/useEffectAfterMount";

import { usePopupActions, usePopupState } from "@store/popupStore";

import theme from "@theme/index";

import { extractTextFromJsx } from "@utilities/extractStringFromJsx";

/**
 * `Modal` component for displaying a dialog window.
 *
 * The open state is controlled by a central store. This allows for a single modal component within the entire app
 * whose content and visibility can be set when the modal is opened and reset on subsequent calls.
 * Displays a dialog window with a title, content, and close button.
 *
 * @returns The Modal dialog component.
 */
export default function Modal() {
  const { modal } = usePopupState();
  const PopupActions = usePopupActions();

  const { content, isOpen, title } = modal;

  /**
   * Closes the modal and executes the tracking function.
   */
  const handleClose = () => {
    PopupActions.closeModal();
  };

  return isOpen ? (
    <Dialog
      aria-labelledby="dialog-title"
      maxWidth="md"
      onClose={handleClose}
      open={isOpen}
      sx={{ "& .MuiDialog-paper": { borderRadius: "8px", overflow: "hidden" } }}
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.borderColor.dark}`,
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        {title}
        <IconButton aria-label="close" color="inherit" edge="start" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
    </Dialog>
  ) : (
    <div />
  );
}
